import React from 'react'
import { Toggle, Badge, Checkbox } from './ui'
import { RESTART_BEHAVIOUR_OPTIONS } from '../utils/settingValues'

/**
 * Intune's Requirements blade (spec §6.7) — the mock's `AdvancedSettings`
 * (sap-mockup/components/AdvancedSettings.tsx).
 *
 * Shared shape so the deployment-template wizard and, later, a live deployment cannot drift: a
 * field added here appears in both. `readOnly` seals it, the way the assignment blade reports
 * itself.
 *
 * The vocabulary is the BACKEND's, not invented here: `validate_requirements`
 * (services/template_service.py:1138) is the authority, and two of its rules are load-bearing:
 *
 *   - `minimumOs` is REQUIRED and has no blank option. Intune rejects a win32LobApp with no
 *     minimumSupportedOperatingSystem, so defaulting it silently would produce a template that
 *     validates here and fails at publish — the worst place to discover it.
 *   - `architectures: []` MEANS "install on all systems". It is a real answer, not a missing one,
 *     which is why the toggle below writes an empty list rather than leaving the field unset.
 */

/** Copied from the mock (:23-39). Kept in its order — newest last is how Intune lists them. */
export const MIN_OS = [
  'Windows 10 1607',
  'Windows 10 1709',
  'Windows 10 1803',
  'Windows 10 1809',
  'Windows 10 1903',
  'Windows 10 1909',
  'Windows 10 2004',
  'Windows 10 20H2',
  'Windows 10 21H1',
  'Windows 10 21H2',
  'Windows 10 22H2',
  'Windows 11 21H2',
  'Windows 11 22H2',
  'Windows 11 23H2',
  'Windows 11 24H2',
] as const

export type Arch = 'x86' | 'x64' | 'arm64'

/** The mock's labels (:41-45) — Intune's own wording on the Requirements blade. */
const ARCHITECTURES: { value: Arch; label: string }[] = [
  { value: 'x86', label: 'Install on x86 system' },
  { value: 'x64', label: 'Install on x64 system' },
  { value: 'arm64', label: 'Install on ARM64 system' },
]

/** file | registry | script, as the validator accepts (_RULE_TYPES, template_service.py:1135). */
export type RuleType = 'file' | 'registry' | 'script'

/** The mock's own operator lists (its AdvancedSettings.tsx:521-528). `requirement` and `value`
 *  have been on RequirementRule since it was written and had NO control: every rule saved was a
 *  bare path with nothing to test about it. */
const RULE_OPS: { value: string; label: string }[] = [
  { value: 'exists', label: 'Exists' },
  { value: 'notExists', label: 'Does not exist' },
  { value: 'version', label: 'Version comparison' },
  { value: 'equals', label: 'Equals' },
]

/** The mock's label is "Script exit code is 0" (its AdvancedSettings.tsx:522). That is not what
 *  Intune does, and the difference now matters: a requirement script is evaluated on what it WRITES
 *  to stdout, compared against the rule's comparisonValue ("true"). Exit code 0 is a precondition,
 *  not the test — so a script that just does `exit 0` and prints nothing makes the app applicable
 *  NOWHERE, silently, because Not-Applicable is not an error.
 *
 *  The label follows the mock; the hint says what the script must actually do. Until this week these
 *  rules were being sent in a Graph property that was ignored, so nobody could have hit it. */
const SCRIPT_OPS: { value: string; label: string }[] = [
  { value: 'scriptExitCode', label: 'Script exit code is 0' },
]

const SCRIPT_OP_HINT =
  'Intune compares what the script WRITES, not its exit code. It must output exactly true or ' +
  'false — a script that only exits 0 makes the app applicable to no device.'

/** Only these two carry a comparison value; the other two are presence tests. */
const OPS_WITH_VALUE = ['version', 'equals']

export interface RequirementRule {
  type: RuleType
  path?: string
  keyPath?: string
  valueName?: string
  requirement?: string
  value?: string
  scriptContent?: string
  runAs32Bit?: boolean
  runAsLoggedOnUser?: boolean
}

export interface IntuneRequirements {
  architectures: Arch[]
  minimumOs: string
  diskSpaceMb: number | null
  memoryMb: number | null
  logicalProcessors: number | null
  cpuSpeedMhz: number | null
  rules: RequirementRule[]
}

export const emptyRequirements = (): IntuneRequirements => ({
  // The reference's own defaults (its AdvancedSettings.tsx:58). An x86-only device is reported
  // Not applicable rather than failed, and the Architecture control on this step says so.
  architectures: ['x64', 'arm64'],
  minimumOs: MIN_OS[0],
  diskSpaceMb: null,
  memoryMb: null,
  logicalProcessors: null,
  cpuSpeedMhz: null,
  rules: [],
})


/** The mock's options, verbatim (its AdvancedSettings.tsx:318-322) — Intune's own three. */
/** The reference's `RULE_KIND` (its AdvancedSettings.tsx): one add button each, so the kind is
 *  chosen by what you press rather than corrected afterwards. */
const RULE_KINDS: { value: RuleType; label: string }[] = [
  { value: 'file', label: 'File' },
  { value: 'registry', label: 'Registry' },
  { value: 'script', label: 'Script' },
]

export const RESTART_BEHAVIOURS = RESTART_BEHAVIOUR_OPTIONS

/** What each restart behaviour does on the device, as Intune applies it. */
export const RESTART_BEHAVIOUR_HINT: Record<string, string> = {
  basedOnReturnCode:
    "Intune decides from the installer's return code: a code mapped to a restart below restarts the device, after Intune's restart notification.",
  suppress:
    'Intune does not restart the device, even after an install that asks for a reboot (3010). The new version is active once the person restarts it.',
  allow:
    'The installer may restart the device on its own, without Intune asking first. Intune does not restart it otherwise.',
  force: 'Intune restarts the device after every install, whatever the return code.',
}

/** The mock's list (:48). */

/**
 * Intune's return-code table and its restart handling — both EDITABLE, matching the reference
 * model.
 *
 * They were shown as fixed for reasons that no longer hold. Return codes are a genuine need: a
 * vendor exiting a code Intune does not know has nowhere else to declare what it means. Restart is
 * the riskier one, so the interface says what each choice does on the device.
 *
 * The publisher sends the chosen value to Intune exactly (deviceRestartBehavior), so what this
 * control says is what the device does.
 */
export type RestartBehaviour = 'suppress' | 'basedOnReturnCode' | 'allow' | 'force'
export type ReturnCode = { returnCode: number; type: string }

/** Intune's own interpretations, in its own spelling. */
export const RETURN_CODE_TYPE_VALUES = [
  'success', 'softReboot', 'hardReboot', 'retry', 'failed',
] as const
const RETURN_CODE_TYPE_LABEL: Record<string, string> = {
  success: 'Success',
  softReboot: 'Soft reboot',
  hardReboot: 'Hard reboot',
  retry: 'Retry',
  failed: 'Failed',
}

export const DEFAULT_RESTART_BEHAVIOUR: RestartBehaviour = 'basedOnReturnCode'

/** What the publisher sends when a template states nothing — the value a new template starts
 *  from, not a constant it can never move off. */
export const defaultReturnCodes = (): ReturnCode[] => [
  { returnCode: 0, type: 'success' },
  { returnCode: 1641, type: 'hardReboot' },
  { returnCode: 3010, type: 'softReboot' },
  { returnCode: 1707, type: 'success' },
  { returnCode: 1618, type: 'retry' },
]

/** The mock's own one-line hardware read-out (its AdvancedSettings.tsx hardwareSummary). The
 *  Overview listed each dimension on its own row, which turns four optional values into four rows
 *  of "—" on the common template that sets none of them. */
export function hardwareSummary(r: IntuneRequirements): string {
  const parts = [
    r.diskSpaceMb != null && `${r.diskSpaceMb} MB disk`,
    r.memoryMb != null && `${r.memoryMb} MB memory`,
    r.logicalProcessors != null && `${r.logicalProcessors} logical processors`,
    r.cpuSpeedMhz != null && `${r.cpuSpeedMhz} MHz`,
  ].filter(Boolean) as string[]
  return parts.length ? parts.join(' · ') : 'No hardware requirement'
}

const inputCls =
  'px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary disabled:opacity-60 text-sm w-56 max-w-full'
const wideCls =
  'w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary disabled:opacity-60 text-sm'

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({
  label,
  hint,
  children,
}) => (
  <div className="mb-4">
    <label className="label">{label}</label>
    {children}
    {hint && <p className="mt-1 text-xs text-theme-text-tertiary">{hint}</p>}
  </div>
)

/** `null` means "no requirement" — distinct from 0, which is a real answer Intune accepts. */
const num = (v: string): number | null => {
  const t = v.trim()
  if (t === '') return null
  const n = Number(t)
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null
}

/** A labelled control inside a requirement rule. The mock's `Field compact`. */
const RuleField: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({
  label,
  hint,
  children,
}) => (
  <label className="block">
    <span className="label">{label}</span>
    {hint && <span className="mb-1 block text-[11px] text-theme-text-tertiary">{hint}</span>}
    {children}
  </label>
)

/** The program half of the step: how Intune reads the installer's exit code, and who handles the
 *  restart. Separate from requirements because they answer different questions — one is "does this
 *  app apply here", the other "what happened when it ran". */
export interface AdvancedProgram {
  restartBehaviour: RestartBehaviour
  returnCodes: ReturnCode[]
}

/** One Graph requirement rule on a published app, as Intune returns it in `requirementRules`. */
export type GraphRequirementRule = Record<string, any>

/** The kind of a Graph requirement rule, named as Intune's Requirements blade names it. */
export function requirementRuleKind(rule: GraphRequirementRule): string {
  const t = String(rule?.['@odata.type'] ?? '')
  if (/PowerShellScript/i.test(t)) return 'script'
  if (/Registry/i.test(t)) return 'registry'
  if (/FileSystem/i.test(t)) return 'file'
  return 'rule'
}

/** What a Graph requirement rule tests, in one line. */
function requirementRuleTarget(rule: GraphRequirementRule): string {
  switch (requirementRuleKind(rule)) {
    case 'script':
      return rule.displayName || 'Requirement script'
    case 'registry':
      return [rule.keyPath, rule.valueName].filter(Boolean).join('\\') || 'Registry key'
    case 'file':
      return [rule.path, rule.fileOrFolderName].filter(Boolean).join('\\') || 'File or folder'
    default:
      return String(rule?.['@odata.type'] ?? '').replace('#microsoft.graph.', '') || 'Rule'
  }
}

/** "1 rule on the app (script), kept unchanged" — the line the read-only list leads with. */
export function existingRulesSummary(rules: GraphRequirementRule[]): string {
  const kinds = Array.from(new Set(rules.map(requirementRuleKind)))
  return `${rules.length} rule${rules.length === 1 ? '' : 's'} on the app (${kinds.join(', ')}), kept unchanged`
}

export interface AdvancedSettingsProps {
  value: IntuneRequirements
  onChange: (next: IntuneRequirements) => void
  program: AdvancedProgram
  onProgramChange: (next: AdvancedProgram) => void
  readOnly?: boolean
  /**
   * Requirement rules already on a published app, in Graph's shape. Shown read-only: saving
   * requirement rules replaces all of them, so while any exist the editor adds none and the save
   * leaves them as they are.
   */
  existingRules?: GraphRequirementRule[]
}

const AdvancedSettings: React.FC<AdvancedSettingsProps> = ({
  value, onChange, program, onProgramChange, readOnly, existingRules = [],
}) => {
  const keepsExisting = existingRules.length > 0
  const r = value
  const set = <K extends keyof AdvancedProgram>(k: K, v: AdvancedProgram[K]) =>
    onProgramChange({ ...program, [k]: v })
  const patch = (p: Partial<IntuneRequirements>) => onChange({ ...r, ...p })
  // Empty list == "all systems", so the toggle is on precisely when the list is non-empty.
  const checkArch = r.architectures.length > 0

  const toggleArch = (a: Arch) =>
    patch({
      architectures: r.architectures.includes(a)
        ? r.architectures.filter((x) => x !== a)
        : [...r.architectures, a],
    })

  const setRule = (i: number, p: Partial<RequirementRule>) =>
    patch({ rules: r.rules.map((x, j) => (j === i ? { ...x, ...p } : x)) })

  return (
    <div className="space-y-5">
      <div>
        <p className="mb-1 text-sm font-semibold text-theme-text-primary">Requirements</p>
        <p className="mb-3 text-[12px] text-theme-text-tertiary">
          What a device must be before this app is offered to it. A device that fails any of these
          reports <span className="text-theme-text-primary">Not applicable</span> — it is never an
          install failure.
        </p>
      </div>

      <section className="mb-6">
        <p className="mb-2 text-sm font-medium text-theme-text-primary">
          Operating system architecture
        </p>
        <div className="rounded-xl border border-theme-border-primary bg-theme-secondary p-4">
          <Checkbox
            checked={checkArch}
            disabled={readOnly}
            onChange={(v) => patch({ architectures: v ? ['x64', 'arm64'] : [] })}
            label="Check operating system architecture"
            hint="Off means the app installs on all systems — Intune's second radio button."
          />
          {checkArch && (
            <div className="ml-6 mt-2 space-y-1.5 border-l border-theme-border-primary pl-4">
              {ARCHITECTURES.map((a) => (
                <Checkbox
                  key={a.value}
                  checked={r.architectures.includes(a.value)}
                  disabled={readOnly}
                  onChange={() => toggleArch(a.value)}
                  label={a.label}
                />
              ))}
              {r.architectures.length === 0 && (
                <p className="text-[12px] text-amber-500">
                  Pick at least one, or turn the check off — no architecture selected makes the app
                  applicable to nothing.
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      <Field
        label="Minimum operating system"
        hint="Required by Intune — there is no blank option."
      >
        <select
          className={inputCls}
          disabled={readOnly}
          value={r.minimumOs || MIN_OS[0]}
          onChange={(e) => patch({ minimumOs: e.target.value })}
        >
          {MIN_OS.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </Field>

      <section className="mb-6">
        <p className="mb-2 text-sm font-medium text-theme-text-primary">Hardware</p>
        <p className="mb-3 text-[12px] text-theme-text-tertiary">
          Leave blank for no requirement. Zero is not the same answer — it is a requirement of
          zero.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Disk space required (MB)">
            <input
              type="number"
              min={0}
              className={inputCls}
              disabled={readOnly}
              value={r.diskSpaceMb ?? ''}
              onChange={(e) => patch({ diskSpaceMb: num(e.target.value) })}
            />
          </Field>
          <Field label="Physical memory required (MB)">
            <input
              type="number"
              min={0}
              className={inputCls}
              disabled={readOnly}
              value={r.memoryMb ?? ''}
              onChange={(e) => patch({ memoryMb: num(e.target.value) })}
            />
          </Field>
          <Field label="Minimum number of logical processors">
            <input
              type="number"
              min={0}
              className={inputCls}
              disabled={readOnly}
              value={r.logicalProcessors ?? ''}
              onChange={(e) => patch({ logicalProcessors: num(e.target.value) })}
            />
          </Field>
          <Field label="Minimum CPU speed required (MHz)">
            <input
              type="number"
              min={0}
              className={inputCls}
              disabled={readOnly}
              value={r.cpuSpeedMhz ?? ''}
              onChange={(e) => patch({ cpuSpeedMhz: num(e.target.value) })}
            />
          </Field>
        </div>
      </section>

      <section className="mb-6">
        <div className="mb-2 flex items-center justify-between gap-3">
          <p className="text-sm font-medium text-theme-text-primary">Additional requirement rules</p>
          {!readOnly && !keepsExisting && (
            <div className="flex flex-wrap gap-2">
              {RULE_KINDS.map((k) => (
                <button
                  key={k.value}
                  type="button"
                  onClick={() =>
                    patch({
                      rules: [
                        ...r.rules,
                        // Intune pre-fills a new rule differently per kind, and so does the
                        // reference: a script rule has one operator and no value to compare.
                        {
                          type: k.value,
                          requirement: k.value === 'script' ? 'scriptExitCode' : 'exists',
                        },
                      ],
                    })
                  }
                  className="rounded-lg border border-theme-border-primary px-2.5 py-1.5 text-[13px] text-theme-text-primary hover:bg-theme-tertiary"
                >
                  + {k.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <p className="mb-3 text-[12px] text-theme-text-tertiary">
          Every rule must pass. Useful for a prerequisite the hardware fields cannot express.
        </p>
        {keepsExisting && (
          <div
            className="mb-3 rounded-lg border border-theme-border-primary bg-theme-tertiary p-3"
            data-testid="existing-requirement-rules"
          >
            <p className="text-[13px] font-medium text-theme-text-primary">
              {existingRulesSummary(existingRules)}
            </p>
            <ul className="mt-2 space-y-1">
              {existingRules.map((rule, i) => (
                <li key={i} className="flex gap-2 text-[12px] text-theme-text-secondary">
                  <span className="w-16 shrink-0 text-theme-text-tertiary">
                    {requirementRuleKind(rule)}
                  </span>
                  <span className="min-w-0 break-all font-mono">{requirementRuleTarget(rule)}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-[12px] text-theme-text-tertiary">
              Saving here leaves these rules as they are. Change them in the Intune admin center.
            </p>
          </div>
        )}
        {keepsExisting ? null : r.rules.length === 0 ? (
          <p className="rounded-lg border border-dashed border-theme-border-primary px-3 py-3 text-[13px] text-theme-text-tertiary">
            No requirements are specified.
          </p>
        ) : (
          <div className="space-y-3">
            {r.rules.map((rule, i) => (
              <div
                key={i}
                className="rounded-lg border border-theme-border-primary bg-theme-tertiary p-3"
              >
                <div className="grid gap-3 sm:grid-cols-[minmax(0,7rem)_minmax(0,1fr)]">
                  {/* Labelled, like every field in the mock's RequirementRuleRow (its
                      AdvancedSettings.tsx:533). These were bare inputs carrying a placeholder,
                      which disappears the moment anything is typed into them. */}
                  <label className="flex-1">
                    <span className="label">
                      Type
                    </span>
                    <select
                      className={inputCls}
                      disabled={readOnly}
                      value={rule.type}
                      onChange={(e) => {
                        const t = e.target.value as RuleType
                        // A script rule has exactly one operator; anything carried over from
                        // the previous type would be published as a rule nobody chose.
                        setRule(i, {
                          type: t,
                          requirement: t === 'script' ? 'scriptExitCode' : 'exists',
                          value: undefined,
                        })
                      }}
                    >
                      <option value="file">File</option>
                      <option value="registry">Registry</option>
                      <option value="script">Script</option>
                    </select>
                  </label>
                </div>

                {rule.type === 'file' && (
                  <RuleField label="Path">
                    <input
                      className={wideCls}
                      disabled={readOnly}
                      placeholder="%ProgramFiles%\\Vendor\\app.exe"
                      value={rule.path ?? ''}
                      onChange={(e) => setRule(i, { path: e.target.value })}
                    />
                  </RuleField>
                )}
                {rule.type === 'registry' && (
                  <div className="grid gap-2 sm:grid-cols-2">
                    <RuleField label="Key path">
                      <input
                        className={wideCls}
                        disabled={readOnly}
                        placeholder="HKEY_LOCAL_MACHINE\\SOFTWARE\\Vendor"
                        value={rule.keyPath ?? ''}
                        onChange={(e) => setRule(i, { keyPath: e.target.value })}
                      />
                    </RuleField>
                    <RuleField label="Value name">
                      <input
                        className={wideCls}
                        disabled={readOnly}
                        placeholder="(Default)"
                        value={rule.valueName ?? ''}
                        onChange={(e) => setRule(i, { valueName: e.target.value })}
                      />
                    </RuleField>
                  </div>
                )}
                {rule.type === 'script' && (
                  <>
                    {/* The mock asks for a "Script file" name (its :542). We take the CONTENT,
                        because Graph's win32LobAppPowerShellScriptRequirement carries
                        scriptContent as base64 — a filename would have nothing to send. */}
                    <span className="label">
                      Script
                    </span>
                    <textarea
                      className={`${wideCls} min-h-[80px] resize-y font-mono text-[12px]`}
                      disabled={readOnly}
                      placeholder="# exit 0 when the device is applicable"
                      value={rule.scriptContent ?? ''}
                      onChange={(e) => setRule(i, { scriptContent: e.target.value })}
                    />
                    <div className="mt-3 space-y-1.5 border-t border-theme-border-primary pt-2.5">
                      <Checkbox
                        checked={!!rule.runAs32Bit}
                        disabled={readOnly}
                        onChange={(v) => setRule(i, { runAs32Bit: v })}
                        label="Run script as 32-bit process on 64-bit clients"
                      />
                      <Checkbox
                        checked={!!rule.runAsLoggedOnUser}
                        disabled={readOnly}
                        onChange={(v) => setRule(i, { runAsLoggedOnUser: v })}
                        label="Run this script using the logged-on credentials"
                      />
                    </div>
                  </>
                )}

                {/* Requirement + Value: the operator, which had no control at all. Without one a
                    file rule says only "this path was mentioned" — the mock's :572-589. */}
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <RuleField
                    label="Requirement"
                    hint={rule.type === 'script' ? SCRIPT_OP_HINT : undefined}
                  >
                    <select
                      className={inputCls}
                      disabled={readOnly}
                      value={
                        rule.type === 'script'
                          ? 'scriptExitCode'
                          : (rule.requirement ?? 'exists')
                      }
                      onChange={(e) => setRule(i, { requirement: e.target.value })}
                    >
                      {(rule.type === 'script' ? SCRIPT_OPS : RULE_OPS).map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </RuleField>
                  {OPS_WITH_VALUE.includes(rule.requirement ?? '') && rule.type !== 'script' && (
                    <RuleField label="Value">
                      <input
                        className={wideCls}
                        disabled={readOnly}
                        placeholder="1.0.0"
                        value={rule.value ?? ''}
                        onChange={(e) => setRule(i, { value: e.target.value })}
                      />
                    </RuleField>
                  )}
                </div>

                {!readOnly && (
                  <div className="mt-2.5 flex justify-end border-t border-theme-border-primary pt-2.5">
                    <button
                      type="button"
                      title="Remove requirement"
                      aria-label="Remove requirement"
                      onClick={() => patch({ rules: r.rules.filter((_, j) => j !== i) })}
                      className="rounded-md px-2 py-1 text-xs text-theme-text-tertiary hover:bg-red-500/10 hover:text-red-500"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Intune's Program blade: what a device does after the install, and what each of the
          installer's exit codes means. Restart still has a single owner at publish
          (resolve_device_restart_behavior) -- the warning below says so where the choice is made. */}
      <section>
        <p className="mb-1 text-sm font-semibold text-theme-text-primary">Install result</p>
        <p className="mb-3 text-[12px] text-theme-text-tertiary">
          What the installer's exit code means, and who handles a restart.
        </p>

        <div className="mb-4 rounded-xl border border-theme-border-primary bg-theme-secondary p-4">
          <Field
            label="Device restart behaviour"
            hint={
              RESTART_BEHAVIOUR_HINT[program.restartBehaviour] ??
              RESTART_BEHAVIOUR_HINT.basedOnReturnCode
            }
          >
            <select
              className={wideCls}
              value={program.restartBehaviour}
              disabled={readOnly}
              onChange={(e) => set('restartBehaviour', e.target.value as RestartBehaviour)}
            >
              {RESTART_BEHAVIOURS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="rounded-xl border border-theme-border-primary bg-theme-secondary p-4">
          <div className="mb-1 flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-theme-text-primary">Return codes</p>
              <p className="text-[12px] text-theme-text-tertiary">
                How Intune reads what the installer exited with. Anything not listed counts as a
                failure.
              </p>
            </div>
            {!readOnly && (
              <button
                type="button"
                onClick={() =>
                  set('returnCodes', [...program.returnCodes, { returnCode: 0, type: 'success' }])
                }
                className="shrink-0 rounded-lg border border-theme-border-primary px-2.5 py-1 text-[12px] text-theme-text-primary hover:bg-theme-tertiary"
              >
                Add
              </button>
            )}
          </div>
          {program.returnCodes.length === 0 && (
            <p className="mt-3 rounded-lg border border-dashed border-theme-border-primary px-3 py-3 text-[13px] text-theme-text-tertiary">
              None mapped — every exit code counts as a failure.
            </p>
          )}
          <div className="mt-3 grid grid-cols-[7rem_1fr_auto] gap-2">
            <span className="label">
              Return code
            </span>
            <span className="label">
              Code type
            </span>
            <span />
            {program.returnCodes.map((rc, i) => (
              <React.Fragment key={i}>
                <input
                  type="number"
                  className={`${inputCls} w-full font-mono`}
                  value={rc.returnCode}
                  disabled={readOnly}
                  onChange={(e) => {
                    const next = [...program.returnCodes]
                    next[i] = { ...rc, returnCode: Number(e.target.value) }
                    set('returnCodes', next)
                  }}
                />
                <select
                  className={wideCls}
                  value={rc.type}
                  disabled={readOnly}
                  onChange={(e) => {
                    const next = [...program.returnCodes]
                    next[i] = { ...rc, type: e.target.value }
                    set('returnCodes', next)
                  }}
                >
                  {RETURN_CODE_TYPE_VALUES.map((t) => (
                    <option key={t} value={t}>
                      {RETURN_CODE_TYPE_LABEL[t]}
                    </option>
                  ))}
                </select>
                {readOnly ? (
                  <span />
                ) : (
                  <button
                    type="button"
                    title="Remove return code"
                    aria-label={`Remove return code ${rc.returnCode}`}
                    onClick={() => set('returnCodes', program.returnCodes.filter((_, j) => j !== i))}
                    className="rounded-lg border border-theme-border-primary px-2 text-theme-text-tertiary hover:text-red-500"
                  >
                    ×
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>
          {!program.returnCodes.some((c) => c.type === 'success') && (
            <p className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2 text-[12px] text-amber-500">
              No success code, so Intune reports every install as a failure. Add one.
            </p>
          )}
        </div>
      </section>
    </div>
  )
}

export default AdvancedSettings
